/**
 * Nasdaq 100 GIFT fund landing layout.
 *
 * Belongs here: hero, benefit sheet, and footer markup.
 * Does not belong here: status or CTA behaviour.
 */
import RenderHTML from '../../../components/ui-components/RenderHTML'
import { getBottomPadding, getTopPadding } from '../../../utils/sessionStorage'
import { PPFAS_PRIME_EXCLUSIVE_SVG } from '../bottom-sheet/PpfasGiftFundOnboardedBottomSheet'
import { PpfasGiftFundSheetCtas } from '../bottom-sheet/PpfasGiftFundSheetCtas'
import {
  startProgressiveImage,
  useProgressiveImage,
  type ProgressiveImageSource
} from '../../../utils/image-loading/progressiveImage'
import { usePpfasGiftFundLanding } from './usePpfasGiftFundLanding'

const HERO_SOURCES: ProgressiveImageSource[] = [
  { src: '/images/ppfas-gift-fund/hero.png', scale: 1 },
  { src: '/images/ppfas-gift-fund/hero@2x.png', scale: 2 },
  { src: '/images/ppfas-gift-fund/hero@3x.png', scale: 3 }
]

// App mounts after auth. Start all three hero files as soon as this module loads.
if (typeof window !== 'undefined' && window.location.pathname.includes('ppfas-gift-fund')) {
  startProgressiveImage(HERO_SOURCES)
}

export function PpfasGiftFundLandingPage() {
  const { scrollRef, ctaDocked, showCta, ctaLabel, ctaIcon, handleBack, handleCta } = usePpfasGiftFundLanding()
  const heroSrc = useProgressiveImage(HERO_SOURCES)

  return (
    <div className="relative h-dvh overflow-hidden bg-[#FCF1ED]">
      <div ref={scrollRef} className="no-scrollbar h-full overflow-y-auto overscroll-none">
        <div className="flex min-h-full flex-col">
          <div className="relative h-[296px] overflow-hidden bg-gradient-to-t from-[#FCF1ED] to-[#EDCEC4]">
            {heroSrc ? (
              <img
                src={heroSrc}
                alt=""
                width={531}
                height={567}
                decoding="async"
                className="absolute left-1/2 top-[-60px] block max-w-none -translate-x-1/2"
              />
            ) : null}
          </div>
          <BenefitSheet />
        </div>
      </div>

      <button
        type="button"
        aria-label="Back"
        onClick={handleBack}
        className="absolute left-5 z-30 flex size-10 items-center justify-center border-none bg-transparent p-0 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        style={{ top: `${getTopPadding() + 12}px` }}
      >
        <span aria-hidden="true" className="flex">
          <RenderHTML innerHTMLText={ARROW_LEFT_SVG} className="flex [&_svg]:block" />
        </span>
      </button>

      {showCta ? (
        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-white px-5${ctaDocked ? ' border-t border-[#F0F4F7] pt-4' : ''}`}
          style={{ paddingBottom: `${getBottomPadding() + 16}px` }}
        >
          <div className="pointer-events-auto">
            <PpfasGiftFundSheetCtas
              ctas={[
                {
                  variant: 'primary',
                  testLocator: 'PPFAS_GIFT_FUND_LANDING_CTA',
                  label: ctaLabel,
                  iconSvg: ctaIcon,
                  onClick: handleCta
                }
              ]}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

function BenefitSheet() {
  return (
    <div className="relative z-10 -mt-5 flex flex-1 flex-col">
      <div aria-hidden="true">
        <RenderHTML
          innerHTMLText={SHEET_CURVE_SVG}
          className="block w-full leading-none [&_svg]:block [&_svg]:h-5 [&_svg]:w-full"
        />
      </div>
      <div className="flex flex-1 flex-col bg-white">
        <div className="flex flex-col gap-8 px-5 pb-20 pt-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <div aria-hidden="true">
              <RenderHTML innerHTMLText={PPFAS_PRIME_EXCLUSIVE_SVG} className="flex justify-center" />
            </div>
            <h1 className="m-0 max-w-[321px] text-[22px] font-[500] leading-[120%] text-[#232323]">
              Invest in the US Nasdaq 100 with expert assistance
            </h1>
            <p className="m-0 max-w-[321px] text-[13px] font-[400] leading-[150%] text-[#232323]">
              Access 100 leading US-listed companies through the Parag Parikh Nasdaq 100 GIFT Fund.
            </p>
          </div>
          <ul className="m-0 flex list-none flex-col gap-5 p-0">
            {BENEFITS.map((benefit) => (
              <li key={benefit.lead} className="flex items-start gap-2">
                <span className="flex size-10 shrink-0 items-center justify-center" aria-hidden="true">
                  <RenderHTML innerHTMLText={benefit.iconSvg} className="flex [&_svg]:block" />
                </span>
                <p className="m-0 pt-[3px] text-[14px] font-[400] leading-[140%] text-[#6A6E6E]">
                  <span className="font-[500] text-[#232323]">{benefit.lead}</span>
                  {benefit.rest}
                </p>
              </li>
            ))}
          </ul>
        </div>
        <div aria-hidden="true" style={{ height: `${getBottomPadding() + 88}px` }} />
      </div>
    </div>
  )
}

const ARROW_LEFT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
<path d="M7.19222 15.6641L0.337914 8.81096C0.230841 8.70477 0.145854 8.57845 0.0878572 8.43926C0.0298595 8.30007 0 8.15078 0 8C0 7.84922 0.0298595 7.69993 0.0878572 7.56074C0.145854 7.42156 0.230841 7.29523 0.337914 7.18905L7.19222 0.335908C7.40733 0.12083 7.69909 -2.26622e-09 8.00331 0C8.30753 2.26622e-09 8.59929 0.12083 8.8144 0.335908C9.02952 0.550987 9.15037 0.842696 9.15037 1.14686C9.15037 1.45103 9.02952 1.74274 8.8144 1.95782L4.39338 6.36667C4.35382 6.40867 4.32734 6.46128 4.3172 6.51807C4.30706 6.57487 4.31368 6.63339 4.33626 6.68648C4.35688 6.73415 4.39012 6.7753 4.43239 6.80549C4.47466 6.83568 4.52437 6.85377 4.57616 6.85781H14.8576C15.1606 6.85781 15.4512 6.97815 15.6654 7.19235C15.8796 7.40655 16 7.69707 16 8C16 8.30293 15.8796 8.59345 15.6654 8.80765C15.4512 9.02185 15.1606 9.14219 14.8576 9.14219H4.57616C4.52042 9.142 4.46584 9.15811 4.41915 9.18856C4.37246 9.219 4.33571 9.26244 4.31341 9.31352C4.29083 9.36661 4.28421 9.42513 4.29435 9.48193C4.3045 9.53872 4.33097 9.59133 4.37053 9.63333L8.8144 14.0422C9.02952 14.2573 9.15037 14.549 9.15037 14.8531C9.15037 15.1573 9.02952 15.449 8.8144 15.6641C8.59929 15.8792 8.30753 16 8.00331 16C7.69909 16 7.40733 15.8792 7.19222 15.6641V15.6641Z" fill="#232323"/>
</svg>`

const SHEET_CURVE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="20" viewBox="0 0 375 20" preserveAspectRatio="none" fill="none">
<path d="M0 20A889 889 0 0 1 375 20V40H0Z" fill="white"/>
</svg>`

const ICON_PARTNERSHIP_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="27" height="23" viewBox="0 0 26.6063 22.8213" fill="none">
<path d="M21.4223 16.6829L15.5005 21.0692C14.2484 22.0114 12.5812 22.0733 11.2599 21.2267L1.00017 14.6525" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M25.6061 13.5913L21.4212 16.6685L14.8831 10.0412L12.6111 11.7933C11.582 12.587 10.1018 12.379 9.33097 11.3325C8.62107 10.3687 8.76139 9.02259 9.65478 8.2261L12.597 5.60299C13.5688 4.73661 14.8086 4.23056 16.1088 4.16953L20.4261 3.96694L25.6061 1.00017" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M14.8869 10.0519C16.4559 11.5323 18.681 10.777 19.5083 9.56853" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M1.00017 1.91814L5.20095 4.31366L8.94644 3.62652C10.2155 3.3937 11.5131 3.77704 12.4826 4.67113L13.0153 5.24018" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const ICON_GLOBAL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26.3752 26.375" fill="none">
<path d="M13.1875 25.375C19.9186 25.375 25.375 19.9186 25.375 13.1875C25.375 6.45653 19.9186 1 13.1875 1C6.45653 1 1 6.45653 1 13.1875C1 19.9186 6.45653 25.375 13.1875 25.375Z" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M1.9375 17.8746H5.21875C6.08899 17.8746 6.92359 17.5289 7.53895 16.9135C8.15431 16.2982 8.5 15.4636 8.5 14.5934V11.7809C8.5 10.9106 8.84569 10.076 9.46105 9.46066C10.0764 8.84531 10.911 8.49961 11.7812 8.49961C12.6515 8.49961 13.4861 8.1539 14.1014 7.53855C14.7168 6.92321 15.0625 6.08861 15.0625 5.21837V1.13086" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M25.375 13C24.4362 12.5129 23.3952 12.2558 22.3375 12.25H18.3438C17.4735 12.25 16.6389 12.5957 16.0235 13.2111C15.4082 13.8264 15.0625 14.661 15.0625 15.5313C15.0625 16.4015 15.4082 17.2361 16.0235 17.8515C16.6389 18.4668 17.4735 18.8125 18.3438 18.8125C18.9653 18.8125 19.5616 19.0594 20.0011 19.4989C20.4406 19.9384 20.6875 20.5347 20.6875 21.1562V22.7875" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const ICON_TAX_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22.4286 26.1429" fill="none">
<path d="M7.5 16.3214L10.2857 18.1786L15.8571 10.75" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M16.275 2.85714H19.5714C20.5971 2.85714 21.4286 3.68862 21.4286 4.71429V23.2857C21.4286 24.3114 20.5971 25.1429 19.5714 25.1429H2.85714C1.83148 25.1429 1 24.3114 1 23.2857V4.71429C1 3.68862 1.83146 2.85714 2.85714 2.85714H6.15357" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M14 5.64286H8.42857C7.14647 5.64286 6.10714 4.60353 6.10714 3.32143C6.10714 2.03933 7.14647 1 8.42857 1H14C15.2821 1 16.3214 2.03933 16.3214 3.32143C16.3214 4.60353 15.2821 5.64286 14 5.64286Z" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const ICON_BENEFITS_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="27" height="27" viewBox="0 0 26.6072 26.6072" fill="none">
<path d="M11.0048 1.81691C12.3438 0.727695 14.2634 0.727695 15.6023 1.81691L16.242 2.33727L17.0561 2.20647C18.7603 1.93265 20.4228 2.89244 21.0376 4.50521L21.3314 5.27568L22.1019 5.56947C23.7147 6.18444 24.6745 7.84684 24.4006 9.55102L24.2698 10.3652L24.7902 11.0048C25.8795 12.3438 25.8795 14.2634 24.7902 15.6023L24.2698 16.242L24.4006 17.0561C24.6745 18.7603 23.7147 20.4228 22.1019 21.0376L21.3314 21.3314L21.0376 22.1019C20.4228 23.7147 18.7603 24.6745 17.0561 24.4006L16.242 24.2698L15.6023 24.7902C14.2634 25.8795 12.3438 25.8795 11.0048 24.7902L10.3652 24.2698L9.55102 24.4006C7.84684 24.6745 6.18444 23.7147 5.56947 22.1019L5.27568 21.3314L4.50521 21.0376C2.89244 20.4228 1.93265 18.7603 2.20647 17.0561L2.33727 16.242L1.81691 15.6023C0.727695 14.2634 0.727695 12.3438 1.81691 11.0048L2.33727 10.3652L2.20647 9.55102C1.93265 7.84684 2.89244 6.18444 4.50521 5.56947L5.27568 5.27568L5.56947 4.50521C6.18444 2.89244 7.84684 1.93265 9.55102 2.20647L10.3652 2.33727L11.0048 1.81691Z" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8.57031 18.0366L18.0346 8.57227" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M9.51674 10.4651C10.0394 10.4651 10.4632 10.0414 10.4632 9.51869C10.4632 8.996 10.0394 8.57227 9.51674 8.57227C8.99405 8.57227 8.57031 8.996 8.57031 9.51869C8.57031 10.0414 8.99405 10.4651 9.51674 10.4651Z" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M17.091 18.0354C17.6137 18.0354 18.0374 17.6117 18.0374 17.089C18.0374 16.5663 17.6137 16.1426 17.091 16.1426C16.5683 16.1426 16.1445 16.5663 16.1445 17.089C16.1445 17.6117 16.5683 18.0354 17.091 18.0354Z" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const ICON_CONCIERGE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="22" viewBox="0 0 26.1429 22.4286" fill="none">
<path d="M1 21.4286H25.1429" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M10.2857 1H15.8571" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.0714 4.71429C6.40456 4.71429 1 10.1188 1 16.7857H25.1429C25.1429 10.1188 19.7384 4.71429 13.0714 4.71429Z" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.0714 1V4.71429" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const BENEFITS = [
  {
    iconSvg: ICON_PARTNERSHIP_SVG,
    lead: 'Trusted Partnership',
    rest: " with Parag Parikh, one of India's established asset management brands."
  },
  {
    iconSvg: ICON_GLOBAL_SVG,
    lead: 'Global investing made easy',
    rest: ' without opening a foreign brokerage account.'
  },
  {
    iconSvg: ICON_TAX_SVG,
    lead: 'Simplified Taxation ',
    rest: "with Indian capital gains tax, so you don't have to manage it."
  },
  {
    iconSvg: ICON_BENEFITS_SVG,
    lead: 'Exclusive benefits ',
    rest: 'of specially negotiated investment fees and exchange rates'
  },
  {
    iconSvg: ICON_CONCIERGE_SVG,
    lead: 'Concierge assistance - ',
    rest: 'Our team guides you through the entire investment process.'
  }
]
